import { createModel } from "@rematch/core";
import { KoreModel } from "./";
import { NOTIFICATION_TYPES } from "../../../constants/enum";

export interface Notification {
  type: { id: NOTIFICATION_TYPES; severity: string };
  message: string;
  applicationName: string;
  isDisplayed: boolean;
  isViewed?: boolean;
  timestamp: Date;
}

export interface NewNotifications {
  message: string;
  applicationName: string;
}

const getNewNotification = (type: NOTIFICATION_TYPES, message: string, applicationName: string): Notification => {
  return { type: { id: type.id, severity: type.name }, message, applicationName, isDisplayed: true, timestamp: new Date() };
};

const manageUnique = (type: NOTIFICATION_TYPES, currentState: Notification[], message: string, applicationName: string) => {
  const notificationIndex = currentState.findIndex(
    (notification) => notification.message === message && notification.applicationName === applicationName && notification.type.id === type.id
  );

  if (notificationIndex > -1) {
    const newNotifications = currentState;

    newNotifications[notificationIndex].timestamp = new Date();

    return [...newNotifications];
  } else return [...currentState, getNewNotification(type, message, applicationName)];
};

const model = {
  name: "notifications",
  state: [] as Notification[],
  reducers: {
    addInfo: (currentState: Notification[], { message, applicationName }: NewNotifications) => [
      ...currentState,
      getNewNotification(NOTIFICATION_TYPES.INFO, message, applicationName)
    ],
    addUniqueInfo: (currentState: Notification[], { message, applicationName }: NewNotifications) =>
      manageUnique(NOTIFICATION_TYPES.INFO, currentState, message, applicationName),
    addSuccess: (currentState: Notification[], { message, applicationName }: NewNotifications) => [
      ...currentState,
      getNewNotification(NOTIFICATION_TYPES.SUCCESS, message, applicationName)
    ],
    addUniqueSuccess: (currentState: Notification[], { message, applicationName }: NewNotifications) =>
      manageUnique(NOTIFICATION_TYPES.SUCCESS, currentState, message, applicationName),
    addWarning: (currentState: Notification[], { message, applicationName }: NewNotifications) => [
      ...currentState,
      getNewNotification(NOTIFICATION_TYPES.WARNING, message, applicationName)
    ],
    addUniqueWarning: (currentState: Notification[], { message, applicationName }: NewNotifications) =>
      manageUnique(NOTIFICATION_TYPES.WARNING, currentState, message, applicationName),
    addError: (currentState: Notification[], { message, applicationName }: NewNotifications) => [
      ...currentState,
      getNewNotification(NOTIFICATION_TYPES.ERROR, message, applicationName)
    ],
    addUniqueError: (currentState: Notification[], { message, applicationName }: NewNotifications) =>
      manageUnique(NOTIFICATION_TYPES.ERROR, currentState, message, applicationName),
    setViewed: (currentState: Notification[], index: number) => {
      const currentNotifications = currentState;

      currentNotifications[index].isViewed = true;

      return [...currentNotifications];
    },
    setAllAsViewed: (currentState: Notification[]) => {
      const currentNotifications = currentState;

      currentNotifications.forEach((notification) => (notification.isViewed = true));

      return [...currentNotifications];
    },
    setHidden: (currentState: Notification[], index: number) => {
      const currentNotifications = currentState;

      currentNotifications[index].isDisplayed = false;

      return [...currentNotifications];
    },
    remove: (currentState: Notification[], id: number) => {
      const newNotifications = currentState.filter((notification, index) => index !== id);

      return [...newNotifications];
    },
    removeAll: () => []
  }
};

export const notificationsModel = createModel<KoreModel>()(model);
